import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface LoyaltyLevel {
  id: string;
  level_number: number;
  name: string;
  min_points: number;
  icon_name: string;
}

export const useLoyaltyLevels = () => {
  const [levels, setLevels] = useState<LoyaltyLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('loyalty_levels')
          .select('*')
          .order('level_number', { ascending: true });

        if (fetchError) throw fetchError;
        setLevels(data || []);
      } catch (err) {
        console.error('Error fetching loyalty levels:', err);
        setError('Error al cargar los niveles');
      } finally {
        setLoading(false);
      }
    };

    fetchLevels();
  }, []);

  const getCurrentLevel = (totalPoints: number): LoyaltyLevel | null => {
    if (!levels.length) return null;
    
    // Find the highest level the user has reached
    for (let i = levels.length - 1; i >= 0; i--) {
      if (totalPoints >= levels[i].min_points) {
        return levels[i];
      }
    }
    return levels[0];
  };

  const getNextLevel = (totalPoints: number): LoyaltyLevel | null => {
    if (!levels.length) return null;
    
    const currentLevel = getCurrentLevel(totalPoints);
    if (!currentLevel) return levels[0];
    
    const nextLevelIndex = levels.findIndex(l => l.level_number === currentLevel.level_number + 1);
    return nextLevelIndex >= 0 ? levels[nextLevelIndex] : null;
  };

  const getProgressToNextLevel = (totalPoints: number): { 
    progress: number; 
    pointsInCurrentLevel: number;
    pointsNeeded: number;
    pointsRemaining: number;
  } => {
    const currentLevel = getCurrentLevel(totalPoints);
    const nextLevel = getNextLevel(totalPoints);

    if (!currentLevel) {
      return { progress: 0, pointsInCurrentLevel: 0, pointsNeeded: 100, pointsRemaining: 100 };
    }

    if (!nextLevel) {
      // Max level reached
      return { progress: 100, pointsInCurrentLevel: totalPoints, pointsNeeded: 0, pointsRemaining: 0 };
    }

    const pointsInCurrentLevel = totalPoints - currentLevel.min_points;
    const pointsNeeded = nextLevel.min_points - currentLevel.min_points;
    const pointsRemaining = nextLevel.min_points - totalPoints;
    const progress = Math.min(100, (pointsInCurrentLevel / pointsNeeded) * 100);

    return { progress, pointsInCurrentLevel, pointsNeeded, pointsRemaining };
  };

  return {
    levels,
    loading,
    error,
    getCurrentLevel,
    getNextLevel,
    getProgressToNextLevel,
  };
};
